import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputModule } from '@sharedModule/components/input/input.module';
import {
  BANK_ACCOUNT_TYPE,
  TRANSFER_TYPE,
  TRANSFER_TYPE_VALUE,
} from '@sharedModule/constants/transfer-type.constant';
import { MaterialModule } from '@sharedModule/material/material.module';
import {
  CollectionPoint,
  DeliveryBank,
  DeliveryBranch,
  MobileNetworkOperator,
  NameValueOption,
} from '@sharedModule/models/delivery.model';
import { DeliveryLookupService } from '@sharedModule/services/delivery/delivery-lookup.service';
import { Subject, takeUntil } from 'rxjs';
import { TRANSFER_DETAILS_ATTRIBUTES } from './transfer-details.constant';

interface SelectEvent {
  value: unknown;
  selectOptions: unknown[] | null;
}

/**
 * Reusable sub-form that renders the conditional delivery-detail fields for the
 * chosen `transfer_type_po` (bank-transfer / cash-pickup / mobile-transfer) and
 * populates them via the delivery lookups. Bound to a parent FormGroup that
 * already holds `transfer_type_po` + the delivery controls
 * (see getDeliveryDetailsControls). Used by both the recipient upsert form and
 * the create-transaction new-recipient flow.
 */
@Component({
  selector: 'app-transfer-details',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, InputModule],
  templateUrl: './transfer-details.component.html',
  styleUrl: './transfer-details.component.scss',
})
export class TransferDetailsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() group!: FormGroup;
  @Input() destinationCountry = '';
  @Input() showTransferTypeSelect = true;
  /** External transfer type, used when showTransferTypeSelect is false
   * (e.g. create-transaction, where transfer_type_po lives at the form root). */
  @Input() transferType = '';

  readonly attr = TRANSFER_DETAILS_ATTRIBUTES;
  readonly transferTypes = TRANSFER_TYPE;
  readonly bankAccountTypes = BANK_ACCOUNT_TYPE;
  readonly type = TRANSFER_TYPE_VALUE;

  /** Local widget control for the bank picker (its value is the delivery_bank_id,
   * which is not itself part of the payload). */
  bankCtrl = new FormControl<number | string>('');

  banks: DeliveryBank[] = [];
  branches: DeliveryBranch[] = [];
  collectionStates: NameValueOption[] = [];
  collectionCities: NameValueOption[] = [];
  collectionPoints: CollectionPoint[] = [];
  mobileOperators: MobileNetworkOperator[] = [];

  #destroy$ = new Subject<void>();

  constructor(private deliveryLookup: DeliveryLookupService) {}

  /** The active transfer type — from the in-group control (recipient) or the
   * external @Input (create-transaction). */
  get currentType(): string {
    return this.showTransferTypeSelect
      ? ((this.group?.get('transfer_type_po')?.value as string) ?? '')
      : this.transferType;
  }

  ngOnInit(): void {
    // Edit mode: a transfer type may already be set — load lists + validators.
    if (this.currentType) {
      this.applyValidators(this.currentType);
      this.loadBaseLists(this.currentType);
    }
    if (this.showTransferTypeSelect) {
      this.group
        .get('transfer_type_po')
        ?.valueChanges.pipe(takeUntil(this.#destroy$))
        .subscribe((selected: string) => {
          this.clearDeliveryValues();
          this.applyValidators(selected);
          this.loadBaseLists(selected);
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.group) return;
    const typeChanged =
      changes['transferType'] && !changes['transferType'].firstChange;
    const destChanged =
      changes['destinationCountry'] &&
      !changes['destinationCountry'].firstChange;
    if (typeChanged) {
      this.clearDeliveryValues();
      this.applyValidators(this.currentType);
      this.loadBaseLists(this.currentType);
    } else if (destChanged && this.currentType) {
      this.clearDeliveryValues();
      this.loadBaseLists(this.currentType);
    }
  }

  // ---------- bank-transfer ----------
  onBankSelected(event: SelectEvent): void {
    const bank = (event.selectOptions as DeliveryBank[] | null)?.find(
      b => b.id === event.value
    );
    this.group
      .get('bene_bank_details')
      ?.get('bank')
      ?.setValue(bank?.name ?? '');
    this.branches = [];
    if (typeof event.value === 'number') {
      this.deliveryLookup
        .getDeliveryBranches({ delivery_bank_id: event.value })
        .pipe(takeUntil(this.#destroy$))
        .subscribe(res => {
          if (res.success) this.branches = res.response;
        });
    }
  }

  onBranchSelected(event: SelectEvent): void {
    const branch = (event.selectOptions as DeliveryBranch[] | null)?.find(
      b => b.id === event.value
    );
    if (branch)
      this.group.get('bene_bank_details')?.patchValue({
        bank_branch: branch.name,
        bank_branch_code: branch.branch_code,
        bank_state: branch.state,
        bank_city: branch.city,
      });
  }

  // ---------- cash-pickup ----------
  onCollectionStateSelected(event: SelectEvent): void {
    this.collectionCities = [];
    this.collectionPoints = [];
    this.deliveryLookup
      .getCollectionPointCities({
        destination_country: this.destinationCountry,
        collection_point_state: event.value as string,
      })
      .pipe(takeUntil(this.#destroy$))
      .subscribe(res => {
        if (res.success) this.collectionCities = this.toOptions(res.response);
      });
  }

  onCollectionCitySelected(event: SelectEvent): void {
    this.collectionPoints = [];
    this.deliveryLookup
      .getCollectionPoints({
        destination_country: this.destinationCountry,
        collection_point_state: this.group.get('collection_point_state')
          ?.value as string,
        collection_point_city: event.value as string,
      })
      .pipe(takeUntil(this.#destroy$))
      .subscribe(res => {
        if (res.success) this.collectionPoints = res.response;
      });
  }

  onCollectionPointSelected(event: SelectEvent): void {
    const point = (event.selectOptions as CollectionPoint[] | null)?.find(
      p => p.id === event.value
    );
    if (point)
      this.group.patchValue({
        collection_point_name: point.name,
        collection_point_code: point.code,
        collection_point_proc_bank: point.bank,
        collection_point_address: point.address,
        collection_point_city: point.city,
        collection_point_state: point.state,
        collection_point_tel: point.telephone,
      });
  }

  // ---------- mobile-transfer ----------
  onMobileNetworkSelected(event: SelectEvent): void {
    const operator = this.mobileOperators.find(
      o => o.mobile_network_id === event.value
    );
    this.group.get('mobile_transfer_network')?.setValue(operator?.name ?? '');
  }

  // ---------- helpers ----------
  private loadBaseLists(transferType: string): void {
    if (!this.destinationCountry) return;
    const payload = { destination_country: this.destinationCountry };
    switch (transferType) {
      case this.type.bank:
        this.deliveryLookup
          .getDeliveryBanks(payload)
          .pipe(takeUntil(this.#destroy$))
          .subscribe(res => {
            if (res.success) this.banks = res.response;
          });
        break;
      case this.type.cashPickup:
        this.deliveryLookup
          .getCollectionPointStates(payload)
          .pipe(takeUntil(this.#destroy$))
          .subscribe(res => {
            if (res.success)
              this.collectionStates = this.toOptions(res.response);
          });
        break;
      case this.type.mobile:
        this.deliveryLookup
          .getMobileNetworkOperators(payload)
          .pipe(takeUntil(this.#destroy$))
          .subscribe(res => {
            if (res.success) this.mobileOperators = res.response.mnos;
          });
        break;
    }
  }

  private applyValidators(transferType: string): void {
    const required: Record<string, boolean> = {
      'bene_bank_details.bank_account_number': transferType === this.type.bank,
      'bene_bank_details.bank_branch_id': transferType === this.type.bank,
      collection_point_id: transferType === this.type.cashPickup,
      mobile_transfer_network_id: transferType === this.type.mobile,
      mobile_transfer_number: transferType === this.type.mobile,
    };
    Object.entries(required).forEach(([path, isRequired]) => {
      const control = this.group.get(path);
      if (!control) return;
      control.setValidators(isRequired ? [Validators.required] : []);
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  private clearDeliveryValues(): void {
    this.bankCtrl.reset('', { emitEvent: false });
    this.branches = [];
    this.collectionCities = [];
    this.collectionPoints = [];
    this.group.get('bene_bank_details')?.reset({}, { emitEvent: false });
    [
      'collection_point_id',
      'collection_point_name',
      'collection_point_code',
      'collection_point_proc_bank',
      'collection_point_address',
      'collection_point_city',
      'collection_point_state',
      'collection_point_tel',
      'mobile_transfer_network_id',
      'mobile_transfer_number',
      'mobile_transfer_network',
    ].forEach(key => this.group.get(key)?.reset('', { emitEvent: false }));
  }

  private toOptions(values: string[]): NameValueOption[] {
    return values.map(value => ({ name: value, value }));
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
